import Fbo from 'nanogl/fbo'
import Program from 'nanogl/program'
import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import PixelFormats from 'nanogl-pf'
import Rect from 'nanogl-primitives-2d/rect'
import GLState, { LocalConfig } from 'nanogl-state/GLState'

import { RenderContext } from '@/webgl/core/RenderContext'
import RenderMask from '@/webgl/core/RenderMask'
import RenderPass from '@/webgl/core/RenderPass'
import Programs from '@/webgl/glsl/programs'
import { IRenderable } from '@/webgl/runner/interfaces/IRenderable'

export default class CollectibleGlowRenderer implements IRenderable {
  node: Node
  quad: Rect

  blurProgram: Program
  blitProgram: Program

  cfg: LocalConfig
  rect: Rect
  _sfbo: Fbo
  _vfbo: Fbo
  _hfbo: Fbo

  renderScale: number = 0.25

  private time: number = 0
  private alpha: number = 1

  constructor(readonly gl: GLContext) {
    this.node = new Node()

    const pf = PixelFormats.getInstance(gl)
    const pixelFormat = pf.getRenderableFormat([pf.RGB8])

    this.blurProgram = Programs(gl).get('blur')
    this.blitProgram = Programs(gl).get('glow-screen')

    this._sfbo = new Fbo(gl)
    this._sfbo.attachColor(pixelFormat.format, pixelFormat.type, pixelFormat.internal)
    this._sfbo.resize(4, 4)

    this._vfbo = new Fbo(gl)
    this._vfbo.attachColor(pixelFormat.format, pixelFormat.type, pixelFormat.internal)
    this._vfbo.resize(4, 4)

    this._hfbo = new Fbo(gl)
    this._hfbo.attachColor(pixelFormat.format, pixelFormat.type, pixelFormat.internal)
    this._hfbo.resize(4, 4)

    this.rect = new Rect(this.gl, -1, -1, 2, 2)

    this.quad = new Rect(gl, -1.0, 0, 2.0, 1.0)
    this.cfg = GLState.get(gl).config()
    this.cfg
      .enableStencil(false)
      .enableDepthTest(false)
      .depthMask(false)
      .enableBlend(true)
      .blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
  }

  prepare(context: RenderContext): void {
    const w = context.viewport.width * this.renderScale
    const h = context.viewport.height * this.renderScale

    this._sfbo.resize(w, h)
    this._hfbo.resize(w, h)
    this._vfbo.resize(w, h)

    this._sfbo.defaultViewport()
    this._sfbo.bind()
    this.gl.clearColor(0, 0, 0, 0)
    this._sfbo.clear()
  }

  composite() {
    this.gl.clearColor(0, 0, 0, 0)
    this.rect.bind()
    this.rect.attribPointer(this.blurProgram)
    this.blurProgram.use()

    this._vfbo.bind()
    this._vfbo.clear()
    this._vfbo.defaultViewport()

    this.blurProgram.uDirection(0, 1)
    this.blurProgram.tTex(this._sfbo.getColorTexture())
    this.blurProgram.uDirection(0, 1)
    this.rect.render()

    this._hfbo.bind()
    this._hfbo.clear()
    this._hfbo.defaultViewport()

    this.blurProgram.uDirection(0, 1)
    this.blurProgram.tTex(this._vfbo.getColorTexture())

    this.blurProgram.uDirection(1, 0)
    this.rect.render()
    this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null)
  }

  rttPass() {}

  preRender(dt: number): void {
    this.time += dt * 7.0
    const phase = Math.sin(this.time) * 0.5 + 0.5
    this.alpha = 0.4 + phase * 0.6
  }

  render(context: RenderContext) {
    if ((context.mask & RenderMask.OPAQUE) == 0 || context.pass != RenderPass.COLOR) return
    this.cfg.apply()
    this.blitProgram.use()
    this.blitProgram.tTex(this._hfbo.getColorTexture())
    this.blitProgram.uAlpha(this.alpha)
    this.rect.bind()
    this.rect.attribPointer(this.blitProgram)
    this.rect.render()
  }
}
